import {
  Box,
  Card,
  CardContent,
  Grid2 as Grid,
  Stack,
  Typography,
} from '@mui/material'
import {
  DisplayCoolerTrendingProductsStatsQuery,
  GetDisplayCoolerSummaryStatsQuery,
} from 'graphql/generated-types'

import TrendIndicator from '@/components/common/TrendIndicator'
import DeliciousTable, { HeadCell } from '@/pages/components/DeliciousTable'

import { formatCurrencyOrDash } from '../utils/cooler-detail-formatters'
import CoolerTempAIInsightsTable from './CoolerTempAIInsightsTable'

type AiPlaceholderRow = {
  placeholder: string
}

type TrendingProduct = NonNullable<
  DisplayCoolerTrendingProductsStatsQuery['displayCoolerTrendingProductsStats']
>['mostIncreasingProduct']

type Props = {
  showDemoAiInsights: boolean
  summary: GetDisplayCoolerSummaryStatsQuery['displayCoolerSummaryStats']
  trendingProducts: DisplayCoolerTrendingProductsStatsQuery['displayCoolerTrendingProductsStats']
  loading: boolean
  error?: string
  onComplianceClick?: () => void
  t: (key: string, params?: Record<string, any>) => string
  coolerName: string
}

const statCardSx = { height: '100%' }

const trendingRowGridTemplateColumns = {
  xs: '56px minmax(0,1fr) 44px 84px',
  sm: '72px minmax(0,1fr) 52px 92px',
  lg: '92px minmax(0,1fr) 56px 96px',
}

const TrendingProductRow: React.FC<{
  label: string
  product: TrendingProduct | null | undefined
}> = ({ label, product }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: trendingRowGridTemplateColumns,
      alignItems: 'center',
      gap: { xs: 0.75, sm: 1 },
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      noWrap
      sx={{ minWidth: 0 }}
      title={product?.name || product?.upc || '-'}
    >
      {product?.name || product?.upc || '-'}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right' }}>
      {formatCurrencyOrDash(product?.revenue)}
    </Typography>
    <Box sx={{ minWidth: 0, justifySelf: 'end' }}>
      {product?.revenueTrend === null || product?.revenueTrend === undefined ? (
        <Typography variant="body2" sx={{ textAlign: 'right' }}>
          -
        </Typography>
      ) : (
        <TrendIndicator value={product.revenueTrend} />
      )}
    </Box>
  </Box>
)

const CoolerTopStats: React.FC<Props> = ({
  showDemoAiInsights,
  summary,
  trendingProducts,
  loading,
  coolerName,
  error,
  t,
  onComplianceClick,
}) => {
  const aiHeadCells: HeadCell[] = [
    { id: 'priority', label: t('aiInsightsTable.priorityHeader') },
    { id: 'store', label: t('aiInsightsTable.storeHeader') },
    { id: 'message', label: t('aiInsightsTable.messageHeader') },
    { id: 'type', label: t('aiInsightsTable.typeHeader') },
    { id: 'action', label: t('aiInsightsTable.actionHeader') },
  ]

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
        <Card
          sx={{
            ...statCardSx,
            cursor: onComplianceClick ? 'pointer' : 'default',
          }}
          onClick={onComplianceClick}
        >
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('coolersTable.columns.compliance')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {summary?.complianceScore === null ||
              summary?.complianceScore === undefined
                ? '-'
                : `${Math.round(summary.complianceScore * 100)}%`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
        <Card sx={statCardSx}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('coolersTable.columns.revenue')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                {formatCurrencyOrDash(summary?.revenue)}
              </Typography>
              {summary?.revenueTrend !== null &&
                summary?.revenueTrend !== undefined && (
                  <TrendIndicator value={summary.revenueTrend} suffix="%" />
                )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 6 }}>
        <Card sx={statCardSx}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('coolerTopStats.trendingProducts.title')}
            </Typography>
            <Stack spacing={0.75}>
              <TrendingProductRow
                label={t('coolerTopStats.trendingProducts.increasing')}
                product={trendingProducts?.mostIncreasingProduct}
              />
              <TrendingProductRow
                label={t('coolerTopStats.trendingProducts.decreasing')}
                product={trendingProducts?.mostDecreasingProduct}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={12}>
        {showDemoAiInsights ? ( // TODO: remove this once we fix ai insights
          <CoolerTempAIInsightsTable coolerName={coolerName || ''} />
        ) : (
          <DeliciousTable<AiPlaceholderRow>
            title={t('aiInsightsTable.title')}
            data={[]}
            headCells={aiHeadCells}
            renderRow={() => null}
            loading={loading}
            error={error}
            noDataMessage={t('aiInsightsTable.noData')}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default CoolerTopStats
